import { Injectable, OnDestroy, inject } from '@angular/core';
import { BehaviorSubject, Subject, fromEvent } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SwUpdate, VersionEvent } from '@angular/service-worker';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationDialogService } from './confirmation-dialog.service';

@Injectable({
  providedIn: 'root'
})
export class PwaConnectivityService implements OnDestroy {
  private swUpdate = inject(SwUpdate);
  private translate = inject(TranslateService);
  private confirmationDialogService = inject(ConfirmationDialogService);

  private destroy$ = new Subject<void>();
  public isOnline$ = new BehaviorSubject<boolean>(navigator.onLine);

  private networkCheckInterval: any;
  private updateCheckInterval: any;

  constructor() {
    this.initNetworkListeners();
    this.initPwaUpdateCheck();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopProactivePing();
    if (this.updateCheckInterval) {
      clearInterval(this.updateCheckInterval);
    }
  }

  private initPwaUpdateCheck(): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.swUpdate.versionUpdates.pipe(takeUntil(this.destroy$)).subscribe((event: VersionEvent) => {
      if (event.type === 'VERSION_INSTALLATION_FAILED') {
        this.updateOnlineStatus(false);
      }
    });

    this.swUpdate.unrecoverable.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      console.error('Service Worker unrecoverable state:', event.reason);
      this.updateOnlineStatus(false);
    });

    // Check for updates every 6 hours
    this.updateCheckInterval = setInterval(() => {
      this.swUpdate.checkForUpdate();
    }, 6 * 60 * 60 * 1000);
  }

  private initNetworkListeners(): void {
    fromEvent(window, 'online')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateOnlineStatus(true);
      });
    fromEvent(window, 'offline')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateOnlineStatus(false);
      });

    // Proactive recovery polling
    this.isOnline$.pipe(takeUntil(this.destroy$)).subscribe((isOnline) => {
      if (isOnline) {
        this.stopProactivePing();
      } else {
        this.startProactivePing();
      }
    });
  }

  private updateOnlineStatus(isOnline: boolean): void {
    if (this.isOnline$.value !== isOnline) {
      this.isOnline$.next(isOnline);
    }
  }

  private startProactivePing(): void {
    if (this.networkCheckInterval) {
      return;
    }

    // Increased interval to 10 seconds to reduce network noise
    this.networkCheckInterval = setInterval(async () => {
      // Final guard to ensure we don't ping if status changed elsewhere
      if (this.isOnline$.value) {
        this.stopProactivePing();
        return;
      }

      try {
        // Perform a lightweight HEAD request to check if the server is reachable
        // Use ?ngsw-bypass=true to ensure the Service Worker doesn't serve from cache
        const response = await fetch(
          `${window.location.origin}${window.location.pathname}index.html?t=${Date.now()}&ngsw-bypass=true`,
          {
            method: 'HEAD',
            cache: 'no-cache'
          }
        );

        if (response.ok || response.status === 200) {
          this.updateOnlineStatus(true);
        }
      } catch (e) {
        // Still offline or server unreachable
      }
    }, 10000);
  }

  private stopProactivePing(): void {
    if (this.networkCheckInterval) {
      clearInterval(this.networkCheckInterval);
      this.networkCheckInterval = null;
    }
  }
}
